import { useEffect, useState } from 'react'

export type ToastKind = 'success' | 'error' | 'info'
export interface ToastItem {
  id: number
  kind: ToastKind
  message: string
}

let items: ToastItem[] = []
let nextId = 1
const listeners = new Set<(t: ToastItem[]) => void>()
const emit = () => listeners.forEach((l) => l(items))

export function dismissToast(id: number) {
  items = items.filter((t) => t.id !== id)
  emit()
}

/** Module-level so non-React code (react-query MutationCache) can raise toasts. */
export function pushToast(message: string, kind: ToastKind = 'info') {
  const id = nextId++
  items = [...items, { id, kind, message }]
  emit()
  setTimeout(() => dismissToast(id), 4500)
  return id
}

export const toast = {
  success: (m: string) => pushToast(m, 'success'),
  error: (m: string) => pushToast(m, 'error'),
  info: (m: string) => pushToast(m, 'info'),
}

const STYLES: Record<ToastKind, string> = {
  success: 'bg-emerald-600 text-white',
  error: 'bg-red-600 text-white',
  info: 'bg-brand-800 text-white dark:bg-brand-700',
}
const ICONS: Record<ToastKind, string> = { success: '✓', error: '⚠️', info: 'ℹ️' }

export function Toaster() {
  const [list, setList] = useState<ToastItem[]>(items)
  useEffect(() => {
    listeners.add(setList)
    return () => { listeners.delete(setList) }
  }, [])

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-4 z-[100] flex flex-col items-center gap-2 px-4"
      role="region"
      aria-live="polite"
      aria-label="Notifications"
    >
      {list.map((t) => (
        <button
          key={t.id}
          onClick={() => dismissToast(t.id)}
          className={`pointer-events-auto flex w-full max-w-sm items-center gap-2.5 rounded-2xl px-4 py-3 text-sm font-medium shadow-lift animate-fade-up ${STYLES[t.kind]}`}
        >
          <span aria-hidden>{ICONS[t.kind]}</span>
          <span className="flex-1 text-left">{t.message}</span>
        </button>
      ))}
    </div>
  )
}
